import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  DialogActions,
  DialogContent,
  DialogTitle,
  ModalClose,
  ModalDialog,
  Skeleton,
  Typography,
} from "@mui/joy";
import { useMutation } from "@tanstack/react-query";
import { format, parse } from "date-fns";
import { useMemo } from "react";

import queryClient from "@/core/queryClient";
import { TaskSet } from "@/core/types";
import { useTaskSetQuery } from "@/hooks/tasks";

import api from "../../core/api";
import { useCurrentVenueUuid } from "../../hooks/venues";
import { useModal } from "./useModal";

const useAnnounceMutation = () => {
  return useMutation({
    mutationFn: async ({ setUuid }: { setUuid: string; venueUuid: string }) => {
      return api.tasks.announceTaskSet(setUuid);
    },
    onSuccess: (_, { setUuid, venueUuid }) => {
      queryClient.invalidateQueries({
        queryKey: ["venues", venueUuid, "tasksets"],
      });
      queryClient.invalidateQueries({
        queryKey: ["tasksets", setUuid],
      });
    },
  });
};

const getPreviewBodyText = (taskSet: TaskSet) => {
  if (!taskSet.tasks) {
    return "";
  }
  const setCount = taskSet.tasks.filter((task) => task.type === "SET").length;
  const stripCount = taskSet.tasks.filter((task) => task.type === "STRIP").length;
  const formattedDate = format(parse(taskSet.startDate, "yyyy-MM-dd", new Date()), "do MMM");
  if (taskSet.zone || taskSet.circuit) {
    const name = taskSet.zone ? taskSet.zone.name : taskSet.circuit!.name;
    return `${name} is getting reset, with ${setCount} new routes going up on ${formattedDate}`;
  }
  if (setCount) {
    return `${setCount} new routes are going up on ${formattedDate}`;
  }
  if (stripCount) {
    return `${stripCount} routes are coming down on ${formattedDate}`;
  }
  return `Check it out on ${formattedDate}`;
};

export default function AnnounceSet({ setUuid }: { setUuid: string }) {
  const currentVenueUuid = useCurrentVenueUuid();
  const { data: taskSet } = useTaskSetQuery(setUuid, currentVenueUuid);

  const { close } = useModal();

  const previewText = useMemo(() => (taskSet ? getPreviewBodyText(taskSet) : ""), [taskSet]);
  const mutation = useAnnounceMutation();

  const onSubmit = async () => {
    if (!currentVenueUuid) {
      return;
    }
    await mutation.mutateAsync({ setUuid, venueUuid: currentVenueUuid });
    close();
  };

  return (
    <ModalDialog>
      <ModalClose />
      <DialogTitle>Announce Set</DialogTitle>
      <DialogContent sx={{ maxWidth: 640 }}>
        <Box sx={{ textAlign: "left" }}>
          {mutation.isError && (
            <Alert color="danger">
              An unexpected error occurred. Please try again or contact support.
            </Alert>
          )}
          <Typography textColor="neutral.600" level="body-sm">
            Preview
          </Typography>
          {taskSet?.tasks ? (
            <Card variant="solid" sx={{ backgroundColor: "neutral.800" }}>
              <CardContent>
                <Typography level="title-lg" textColor="inherit" fontWeight="bold">
                  📢 A new set is coming
                </Typography>
                <Typography textColor="inherit">{previewText}</Typography>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Typography level="title-md">
                <Skeleton>📢 A new set is coming</Skeleton>
              </Typography>
              <Typography level="body-md">
                <Skeleton>XXXXX XXXX XXXXXXXX XXX XXXXXXXX XXXXXX XXXX XXXXXX XXXX XXXX</Skeleton>
              </Typography>
            </Card>
          )}
        </Box>
        <Typography textAlign="left" marginTop={1}>
          This notification will be sent immediately to everyone who has climbed at the gym in the
          last 3 months.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          type="submit"
          color="primary"
          sx={{ flex: 1 }}
          loadingPosition="start"
          loading={mutation.isPending}
          onClick={() => onSubmit()}
        >
          Announce
        </Button>
        <Button
          onClick={() => {
            close();
          }}
          color="neutral"
          variant="soft"
          sx={{ flex: 1 }}
        >
          Cancel
        </Button>
      </DialogActions>
    </ModalDialog>
  );
}
